import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { http, unwrap } from './client'
import { retryUnlessRefused, workspacePath } from './workspaces'
import type { ApiEnvelope } from '@/types/api'
import type { OperationsSnapshot } from './snapshot'

/**
 * How far a workspace's own records go back (S0 §21.5, DECISIONS.md #34):
 * nothing recorded yet, a history still being built, or established. Null
 * while its data cannot be read; the health says why.
 */
export type DataStage = 'NO_DATA' | 'BUILDING_HISTORY' | 'ESTABLISHED'

export interface DataHistory {
  stage: DataStage | null
  firstRecordDay: string | null
  completeWeeks: number
  requiredWeeks: number
  /** The day the workspace becomes established; null once it is, or while nothing is recorded. */
  establishedOn: string | null
  /** The first day an objective can be set; null once it has come, or while nothing is recorded. */
  objectivesFrom: string | null
  dataHealth: OperationsSnapshot['dataHealth']
}

export const dataHistoryKeys = {
  of: (workspaceId: string) => ['workspaces', workspaceId, 'data-history'] as const,
}

/** Read again every few minutes: the stage moves on as weeks are recorded, not by the second. */
const REFRESH_MS = 5 * 60_000

export const useDataHistory = (workspaceId: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean> = true) =>
  useQuery({
    queryKey: computed(() => dataHistoryKeys.of(toValue(workspaceId))),
    queryFn: async () => unwrap((await http.get<ApiEnvelope<DataHistory>>(`${workspacePath(toValue(workspaceId))}/data-history`)).data),
    enabled: computed(() => toValue(enabled)),
    refetchInterval: REFRESH_MS,
    retry: retryUnlessRefused,
  })
